import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useCountries, useStatesByCountry, useCitiesByState, extractData, testApiConnectivity, shouldUseBackendSearch } from '../hooks/useLocations';
import { Country, State, City, getCountryById, getStateById, getCityById } from '../services/locationService';
import { CascadeCombo } from './CascadeCombo';
import { Field } from './ui/Field';
import { Input } from './ui/Input';
import { Callout } from './ui/Callout';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';

type DebouncedFn<T extends (...args: never[]) => void> = T & { clear: () => void };

function debounce<T extends (...args: never[]) => void>(fn: T, wait: number): DebouncedFn<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const debounced = ((...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  }) as DebouncedFn<T>;
  debounced.clear = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return debounced;
}

type PagerProps = {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
};

const Pager: React.FC<PagerProps> = ({ page, totalPages, onChange }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 6 }}>
    <Button
      type="button"
      variant="ghost"
      size="xs"
      disabled={page <= 1}
      onClick={() => onChange(Math.max(1, page - 1))}
      aria-label="Previous page"
    >
      <Icon name="chevronLeft" size={12} />
    </Button>
    <span className="muted" style={{ fontSize: 12 }}>
      {page} / {totalPages}
    </span>
    <Button
      type="button"
      variant="ghost"
      size="xs"
      disabled={page >= totalPages}
      onClick={() => onChange(Math.min(totalPages, page + 1))}
      aria-label="Next page"
    >
      <Icon name="chevronRight" size={12} />
    </Button>
  </div>
);

export interface LocationData {
  country_id?: number | null;
  state_id?: number | null;
  city_id?: number | null;
  city_other?: string;
  
  // Legacy fields
  country?: string;
  state?: string;
  city?: string;
}

interface LocationSelectorProps {
  value: LocationData;
  onChange: (data: LocationData) => void;
  errors?: {
    country_id?: string;
    state_id?: string;
    city_id?: string;
    city_other?: string;
  };
  isRTL?: boolean;
  lockedCountryId?: number | null;
  lockedCountryName?: string;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  value,
  onChange,
  errors,
  isRTL = false,
  lockedCountryId,
  lockedCountryName,
}) => {
  // Local state to control UI
  const [showCityOther, setShowCityOther] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [manualCityEntry, setManualCityEntry] = useState(false);
  const [loadingSelectedCountry, setLoadingSelectedCountry] = useState(false);
  const [loadingSelectedState, setLoadingSelectedState] = useState(false);
  const [loadingSelectedCity, setLoadingSelectedCity] = useState(false);
  
  // Search states
  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  
  // Input values for controlled inputs (separate from search to avoid clearing)
  const [countryInputValue, setCountryInputValue] = useState('');
  const [stateInputValue, setStateInputValue] = useState('');
  const [cityInputValue, setCityInputValue] = useState('');
  
  // Pagination states
  const [countryPage, setCountryPage] = useState(1);
  const [statePage, setStatePage] = useState(1);
  const [cityPage, setCityPage] = useState(1);
  const perPage = 15; // Items per page

  const handleChange = useCallback((newData: LocationData) => {
    onChange(newData);
  }, [onChange]);
  
  // Fetch countries with search param if needed
  const {
    data: countriesResponse,
    isLoading: isLoadingCountries,
    isError: isErrorCountries,
    refetch: refetchCountries
  } = useCountries({
    search: countrySearch || undefined,
    page: countryPage,
    per_page: perPage,
    select_country: lockedCountryId || value.country_id || undefined,
  });
  
  // Fetch states based on selected country
  const {
    data: statesResponse,
    isLoading: isLoadingStates,
    isError: isErrorStates,
    refetch: refetchStates
  } = useStatesByCountry(value.country_id || undefined, {
    search: stateSearch || undefined,
    page: statePage,
    per_page: perPage
  });
  
  // Fetch cities based on selected state
  const {
    data: citiesResponse,
    isLoading: isLoadingCities,
    isError: isErrorCities,
    refetch: refetchCities
  } = useCitiesByState(value.state_id || undefined, {
    search: citySearch || undefined,
    page: cityPage,
    per_page: perPage
  });
  
  // Extract data from API responses
  const rawCountries = extractData(countriesResponse);
  const rawStates = extractData(statesResponse);
  const rawCities = extractData(citiesResponse);
  
  // Get pagination metadata
  const countriesMeta = countriesResponse?.data?.meta || { total: 0, last_page: 1 };
  const statesMeta = statesResponse?.data?.meta || { total: 0, last_page: 1 };
  const citiesMeta = citiesResponse?.data?.meta || { total: 0, last_page: 1 };
  
  // Determine if we should use backend or frontend filtering
  const useBackendCountrySearch = shouldUseBackendSearch(countriesMeta.total);
  const useBackendStateSearch = shouldUseBackendSearch(statesMeta.total);
  const useBackendCitySearch = shouldUseBackendSearch(citiesMeta.total);
  
  // Pre-process options to ensure selected values are included
  const countries = useMemo(() => {
    if (selectedCountry && !rawCountries.some((c: Country) => c.id === selectedCountry.id)) {
      return [selectedCountry, ...rawCountries];
    }
    return rawCountries;
  }, [rawCountries, selectedCountry]);
  
  const states = useMemo(() => {
    if (selectedState && !rawStates.some((s: State) => s.id === selectedState.id)) {
      return [selectedState, ...rawStates];
    }
    return rawStates;
  }, [rawStates, selectedState]);
  
  const cities = useMemo(() => {
    if (selectedCity && !rawCities.some((c: City) => c.id === selectedCity.id)) {
      return [selectedCity, ...rawCities];
    }
    return rawCities;
  }, [rawCities, selectedCity]);
  
  // Create debounced search handlers - Fixed implementation
  const debouncedCountrySearch = useMemo(
    () => debounce((searchValue: string) => {
      setCountrySearch(searchValue);
      setCountryPage(1);
    }, 300),
    []
  );
  
  const debouncedStateSearch = useMemo(
    () => debounce((searchValue: string) => {
      setStateSearch(searchValue);
      setStatePage(1);
    }, 300),
    []
  );
  
  const debouncedCitySearch = useMemo(
    () => debounce((searchValue: string) => {
      setCitySearch(searchValue);
      setCityPage(1);
    }, 300),
    []
  );
  
  // Clean up debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedCountrySearch.clear();
      debouncedStateSearch.clear();
      debouncedCitySearch.clear();
    };
  }, [debouncedCountrySearch, debouncedStateSearch, debouncedCitySearch]);
  
  // Fetch selected country by ID if not found in current options
  useEffect(() => {
    if (!lockedCountryId || value.country_id === lockedCountryId) {
      return;
    }

    handleChange({
      ...value,
      country_id: lockedCountryId,
      country: lockedCountryName || value.country || '',
      state_id: null,
      state: '',
      city_id: null,
      city: '',
      city_other: '',
    });
  }, [handleChange, lockedCountryId, lockedCountryName, value]);

  useEffect(() => {
    async function fetchSelectedCountry() {
      if (value.country_id && !selectedCountry && !isLoadingCountries && countries.length > 0) {
        // Check if it's in the current results first
        const foundCountry = countries.find((c: Country) => c.id === value.country_id);
        if (foundCountry) {
          setSelectedCountry(foundCountry);
          setCountryInputValue(foundCountry.name);
          return;
        }
        
        // If not found, fetch it directly
        setLoadingSelectedCountry(true);
        try {
          const country = await getCountryById(value.country_id);
          if (country) {
            setSelectedCountry(country);
            setCountryInputValue(country.name);
          }
        } catch (error) {
          console.error("Error fetching selected country:", error);
        } finally {
          setLoadingSelectedCountry(false);
        }
      }
    }
    
    fetchSelectedCountry();
  }, [value.country_id, countries, selectedCountry, isLoadingCountries]);

  useEffect(() => {
    if (!lockedCountryId || !lockedCountryName) {
      return;
    }

    if (value.country_id === lockedCountryId) {
      setCountryInputValue(lockedCountryName);
    }
  }, [lockedCountryId, lockedCountryName, value.country_id]);
  
  // Fetch selected state by ID if not found in current options
  useEffect(() => {
    async function fetchSelectedState() {
      if (value.country_id && value.state_id && !selectedState && !isLoadingStates && states.length > 0) {
        // Check if it's in the current results first
        const foundState = states.find((s: State) => s.id === value.state_id);
        if (foundState) {
          setSelectedState(foundState);
          setStateInputValue(foundState.name);
          return;
        }
        
        // If not found, fetch it directly
        setLoadingSelectedState(true);
        try {
          const state = await getStateById(value.country_id, value.state_id);
          if (state) {
            setSelectedState(state);
            setStateInputValue(state.name);
          }
        } catch (error) {
          console.error("Error fetching selected state:", error);
        } finally {
          setLoadingSelectedState(false);
        }
      }
    }
    
    fetchSelectedState();
  }, [value.country_id, value.state_id, states, selectedState, isLoadingStates]);
  
  // Fetch selected city by ID if not found in current options
  useEffect(() => {
    async function fetchSelectedCity() {
      if (value.state_id && value.city_id && !selectedCity && !isLoadingCities && cities.length > 0) {
        // Check if it's in the current results first
        const foundCity = cities.find((c: City) => c.id === value.city_id);
        if (foundCity) {
          setSelectedCity(foundCity);
          setCityInputValue(foundCity.name);
          return;
        }
        
        // If not found, fetch it directly
        setLoadingSelectedCity(true);
        try {
          const city = await getCityById(value.state_id, value.city_id);
          if (city) {
            setSelectedCity(city);
            setCityInputValue(city.name);
          }
        } catch (error) {
          console.error("Error fetching selected city:", error);
        } finally {
          setLoadingSelectedCity(false);
        }
      }
    }
    
    fetchSelectedCity();
  }, [value.state_id, value.city_id, cities, selectedCity, isLoadingCities]);
  
  // Check if we need to enable manual city entry
  useEffect(() => {
    // Check if user has a city_other value already
    if (value.city_other && !value.city_id) {
      setManualCityEntry(true);
    }
    
    // If country has no states or state has no cities, suggest manual entry
    if ((value.country_id && states.length === 0 && !isLoadingStates) || 
        (value.state_id && cities.length === 0 && !isLoadingCities)) {
      setShowCityOther(true);
    } else {
      setShowCityOther(false);
    }
  }, [value.country_id, value.state_id, value.city_other, value.city_id, states.length, cities.length, isLoadingStates, isLoadingCities]);
  
  // Reset dependent selections and searches when parent changes
  useEffect(() => {
    // When country changes, reset state and city
    if (value.country_id !== selectedCountry?.id) {
      setSelectedState(null);
      setSelectedCity(null);
      setStateInputValue('');
      setCityInputValue('');
      setStateSearch('');
      setCitySearch('');
      setStatePage(1);
      setCityPage(1);
      setManualCityEntry(false);
    }
  }, [value.country_id, selectedCountry?.id]);
  
  useEffect(() => {
    // When state changes, reset city
    if (value.state_id !== selectedState?.id) {
      setSelectedCity(null);
      setCityInputValue('');
      setCitySearch('');
      setCityPage(1);
      setManualCityEntry(false);
    }
  }, [value.state_id, selectedState?.id]);
  
  // Handle country selection
  const handleCountryChange = (countryId: number | string | null) => {
    if (!countryId) {
      handleChange({
        ...value,
        country_id: null,
        country: '',
        state_id: null,
        state: '',
        city_id: null,
        city: '',
        city_other: ''
      });
      setSelectedCountry(null);
      setCountryInputValue('');
    } else {
      const selectedCountry = countries.find((c: Country) => c.id === countryId);
      if (selectedCountry) {
        handleChange({
          ...value,
          country_id: selectedCountry.id,
          country: selectedCountry.name,
          state_id: null,
          state: '',
          city_id: null,
          city: '',
          city_other: ''
        });
        setSelectedCountry(selectedCountry);
        setCountryInputValue(selectedCountry.name);
      }
    }
    
    // Reset manual city entry when country changes
    setManualCityEntry(false);
  };
  
  // Handle state selection
  const handleStateChange = (stateId: number | string | null) => {
    if (!stateId) {
      handleChange({
        ...value,
        state_id: null,
        state: '',
        city_id: null,
        city: '',
        city_other: ''
      });
      setSelectedState(null);
      setStateInputValue('');
      return;
    }
    
    // Find the state to get its name for legacy field
    const state = states.find((s: State) => s.id === stateId);
    if (state) {
      handleChange({
        ...value,
        state_id: state.id,
        state: state.name,
        city_id: null,
        city: '',
        city_other: ''
      });
      setSelectedState(state);
      setStateInputValue(state.name);
    }
    
    // Reset manual city entry when state changes
    setManualCityEntry(false);
  };
  
  // Handle city selection
  const handleCityChange = (cityId: number | string | null) => {
    if (!cityId) {
      handleChange({
        ...value,
        city_id: null,
        city: '',
        city_other: ''
      });
      setSelectedCity(null);
      setCityInputValue('');
      return;
    }
    
    // Find the city to get its name for legacy field
    const city = cities.find((c: City) => c.id === cityId);
    if (city) {
      handleChange({
        ...value,
        city_id: city.id,
        city: city.name,
        city_other: ''
      });
      setSelectedCity(city);
      setCityInputValue(city.name);
    }
    
    // Reset manual city entry when a city is selected
    setManualCityEntry(false);
  };
  
  // Handle city_other input
  const handleCityOtherChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const cityOther = event.target.value;
    
    handleChange({
      ...value,
      city_id: null,
      city_other: cityOther,
      city: cityOther // Also update the legacy field
    });
    
    // Clear selected city when entering manual city
    setSelectedCity(null);
  };
  
  // Toggle between city selection dropdown and manual entry
  const toggleManualCityEntry = () => {
    const newValue = !manualCityEntry;
    setManualCityEntry(newValue);
    
    // If turning off manual entry, clear the city_other field
    if (!newValue && value.city_other) {
      handleChange({
        ...value,
        city_other: '',
        city: selectedCity?.name || ''
      });
    }
    
    // If turning on manual entry, clear the selected city
    if (newValue && value.city_id) {
      handleChange({
        ...value,
        city_id: null,
        city: '',
      });
      setSelectedCity(null);
      setCityInputValue('');
    }
  };
  
  // Function to test API connectivity and refresh data
  const runApiTest = async () => {
    try {
      const result = await testApiConnectivity();
      refetchCountries();
      if (value.country_id) refetchStates();
      if (value.state_id) refetchCities();
      return result;
    } catch (error) {
      return { success: false, error };
    }
  };
  
  // Force check city_other visibility based on data conditions
  useEffect(() => {
    // Only run this if the component is mounted and data is loaded
    if (isLoadingStates || isLoadingCities) {
      return; // Skip if still loading
    }
    
    // Always show city_other in these cases:
    // 1. If a country is selected but there are no states
    // 2. If a state is selected but there are no cities
    const shouldShow = !!(
      (value.country_id && states.length === 0 && !isLoadingStates) ||
      (value.state_id && cities.length === 0 && !isLoadingCities)
    );
    
    // Only update state if it's different to avoid infinite loop
    if (shouldShow !== showCityOther) {
      setShowCityOther(shouldShow);
    }
  }, [
    value.country_id, 
    value.state_id, 
    states.length, 
    cities.length, 
    isLoadingStates, 
    isLoadingCities,
    showCityOther
  ]);
  
  const errorMessage = isErrorCountries
    ? 'Error loading countries. Please try again.'
    : isErrorStates
      ? 'Error loading states. Please try again.'
      : isErrorCities
        ? 'Error loading cities. Please try again.'
        : null;

  const cityHelper = errors?.city_id
    || (manualCityEntry
      ? 'Manual entry enabled'
      : value.state_id && cities.length === 0 && !isLoadingCities
        ? 'No cities found for this state'
        : undefined);

  return (
    <div>
      {errorMessage && (
        <Callout tone="warn" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>{errorMessage}</span>
          <Button type="button" variant="ghost" size="xs" onClick={runApiTest} aria-label="Retry">
            <Icon name="refresh" size={12} />
            <span>Retry</span>
          </Button>
        </Callout>
      )}

      <div className="grid-3">
        <div>
          <CascadeCombo<Country>
            label="Country"
            required
            value={selectedCountry}
            inputValue={countryInputValue}
            options={countries}
            loading={isLoadingCountries || loadingSelectedCountry}
            disabled={Boolean(lockedCountryId)}
            disableClear={Boolean(lockedCountryId)}
            onChange={(opt) => handleCountryChange(opt ? opt.id : null)}
            onInputChange={(text, isUserInput) => {
              setCountryInputValue(text);
              if (isUserInput) debouncedCountrySearch(text);
            }}
            error={errors?.country_id}
            helperText={errors?.country_id || (lockedCountryId ? 'Country is locked for this flow.' : undefined)}
            placeholder={lockedCountryId ? lockedCountryName || 'Locked country' : 'Select or search country'}
            loadingText="Loading countries…"
            noOptionsText={countrySearch ? 'No countries found' : 'Type to search'}
          />
          {useBackendCountrySearch && countriesMeta?.last_page > 1 && (
            <Pager page={countryPage} totalPages={countriesMeta.last_page} onChange={setCountryPage} />
          )}
        </div>

        <div>
          <CascadeCombo<State>
            label="State / Province"
            required
            value={selectedState}
            inputValue={stateInputValue}
            options={states}
            loading={isLoadingStates || loadingSelectedState}
            disabled={!value.country_id}
            onChange={(opt) => handleStateChange(opt ? opt.id : null)}
            onInputChange={(text, isUserInput) => {
              setStateInputValue(text);
              if (isUserInput && value.country_id) debouncedStateSearch(text);
            }}
            error={errors?.state_id}
            helperText={errors?.state_id}
            placeholder={value.country_id ? 'Select or search state' : 'Select a country first'}
            loadingText="Loading states…"
            noOptionsText={!value.country_id ? 'Select a country first' : stateSearch ? 'No states found' : 'Type to search'}
          />
          {useBackendStateSearch && statesMeta?.last_page > 1 && value.country_id && (
            <Pager page={statePage} totalPages={statesMeta.last_page} onChange={setStatePage} />
          )}
        </div>

        <div>
          <CascadeCombo<City>
            label="City"
            required={!manualCityEntry}
            value={selectedCity}
            inputValue={cityInputValue}
            options={cities}
            loading={isLoadingCities || loadingSelectedCity}
            disabled={!value.state_id || manualCityEntry}
            onChange={(opt) => handleCityChange(opt ? opt.id : null)}
            onInputChange={(text, isUserInput) => {
              setCityInputValue(text);
              if (isUserInput && value.state_id) debouncedCitySearch(text);
            }}
            error={errors?.city_id}
            helperText={cityHelper}
            placeholder={!value.state_id ? 'Select a state first' : manualCityEntry ? 'Manually enter city below' : 'Select or search city'}
            loadingText="Loading cities…"
            noOptionsText={!value.state_id ? 'Select a state first' : citySearch ? 'No cities found' : 'Type to search'}
            endActionIcon={
              value.state_id && !manualCityEntry
                ? { icon: 'edit', onClick: toggleManualCityEntry, title: 'Enter city manually' }
                : undefined
            }
          />

          {manualCityEntry && (
            <div style={{ marginTop: 10 }}>
              <Field
                label="Enter City Name"
                hint={errors?.city_other || 'Type city name manually'}
                htmlFor="location-city-other"
              >
                <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                  <Input
                    id="location-city-other"
                    value={value.city_other || ''}
                    onChange={handleCityOtherChange}
                    placeholder="Type city name"
                    required
                    style={errors?.city_other ? { borderColor: 'var(--err)' } : undefined}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={toggleManualCityEntry}
                    title="Use city selection"
                    aria-label="Use city selection"
                  >
                    <Icon name="mapPin" size={14} />
                  </Button>
                </div>
              </Field>
            </div>
          )}

          {useBackendCitySearch && citiesMeta?.last_page > 1 && value.state_id && (
            <Pager page={cityPage} totalPages={citiesMeta.last_page} onChange={setCityPage} />
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationSelector; 
